import type { AdminAffiliateResponse } from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  MERCHANT_SORT_LABELS,
  MERCHANT_SORTS,
  sortMerchants,
  type MerchantSort,
  amazonEstimate,
  DEFAULT_COMMISSION,
  DEFAULT_CONVERSION,
  QUALIFYING_DAYS,
  QUALIFYING_SALES,
} from "../components/admin/affiliate";
import { Columns, monthLabel, Split } from "../components/admin/charts";
import {
  Box,
  euros,
  Favicon,
  Grid,
  n,
  Pending,
  percent,
  Row,
  Shop,
  Spark,
  Table,
} from "../components/admin/retro";
import { adminAffiliateQuery } from "../lib/queries";

export const Route = createFileRoute("/_authed/admin/sites/")({
  component: AffiliatePage,
});

function AffiliatePage() {
  const affiliate = useQuery(adminAffiliateQuery);
  if (!affiliate.data) return <Pending query={affiliate} />;
  return <Affiliate affiliate={affiliate.data} />;
}

function Affiliate({ affiliate }: { affiliate: AdminAffiliateResponse }) {
  const [commission, setCommission] = useState(DEFAULT_COMMISSION);
  const [conversion, setConversion] = useState(DEFAULT_CONVERSION);
  const estimate = amazonEstimate(affiliate, commission, conversion);
  const qualifies = estimate.salesInWindow >= QUALIFYING_SALES;
  const [sort, setSort] = useState<MerchantSort>("reserved");
  const merchants = sortMerchants(affiliate.merchants, sort);
  const reservedTotal = affiliate.merchants.reduce(
    (total, merchant) => total + merchant.reservedValueCents,
    0,
  );

  return (
    <Grid>
      <Box
        title="par boutique"
        className="md:col-span-2 xl:col-span-4"
        right={
          <span className="inline-flex gap-1">
            {MERCHANT_SORTS.map((option) => (
              <button
                key={option}
                type="button"
                className="retro-btn"
                aria-pressed={sort === option}
                onClick={() => setSort(option)}
              >
                {MERCHANT_SORT_LABELS[option]}
              </button>
            ))}
          </span>
        }
      >
        <div className="mb-2">
          <Split
            parts={[
              ...affiliate.merchants.slice(0, 6).map((merchant) => ({
                label: merchant.merchant,
                icon: <Favicon host={merchant.merchant} />,
                values: [merchant.reservedValueCents],
              })),
              {
                label: "autres",
                values: [
                  affiliate.merchants
                    .slice(6)
                    .reduce((total, merchant) => total + merchant.reservedValueCents, 0),
                ],
                color: "var(--r-line)",
              },
            ]}
          />
        </div>
        <Table
          head={[
            "boutique",
            "valeur réservée",
            "part",
            "cadeaux rés.",
            "envies",
            "listes",
            "prix moy.",
            "clics 90 j",
            "clics 12 m",
            "intentions 12 m",
            "produits cliqués",
          ]}
          rows={merchants.map((merchant) => [
            {
              value: <Shop host={merchant.merchant} />,
              num: false,
              tone: merchant.reservedValueCents > 0 ? undefined : "dim",
            },
            {
              value: merchant.reservedValueCents > 0 ? euros(merchant.reservedValueCents) : "-",
              tone: merchant.reservedValueCents > 0 ? "ok" : "dim",
            },
            percent(merchant.reservedValueCents, reservedTotal),
            merchant.reservedQuantity,
            merchant.wishes,
            merchant.lists,
            merchant.avgPriceCents === null ? "-" : euros(merchant.avgPriceCents),
            merchant.clicks90,
            merchant.clicks365,
            merchant.intents365,
            merchant.products,
          ])}
        />
        <p className="mt-1" style={{ color: "var(--r-dim)" }}>
          Le meilleur signal d'achat est la réservation, pas le clic. Les intentions comptent une
          envie une seule fois par jour, quel que soit le nombre de clics. Les programmes rémunèrent
          tout le panier acheté pendant la durée du cookie : Amazon 24 h (jusqu'à ~89 j si l'article
          est mis au panier), souvent 30 j chez Awin, Effiliation ou Kwanko.
          {affiliate.hiddenLists > 0 &&
            ` Vos ${affiliate.hiddenLists} listes en mode surprise sont exclues des réservations.`}
        </p>
      </Box>
      <Box title="clics sortants par mois" className="md:col-span-2 xl:col-span-3">
        <Columns
          labels={affiliate.months.map((month) => monthLabel(month.month))}
          height={160}
          series={[
            {
              label: "amazon.fr",
              values: affiliate.months.map((month) => month.amazonFr),
              color: "var(--r-amber)",
            },
            {
              label: "autres amazon",
              values: affiliate.months.map((month) => month.amazonOther),
              color: "var(--r-red)",
            },
            {
              label: "autres marchands",
              values: affiliate.months.map(
                (month) => month.clicks - month.amazonFr - month.amazonOther,
              ),
              color: "var(--r-dim)",
            },
          ]}
        />
      </Box>
      <Box title="envies par boutique">
        <Split
          parts={[
            { label: "amazon.fr", values: [affiliate.wishes.amazonFr], color: "var(--r-amber)" },
            {
              label: "autres amazon",
              values: [affiliate.wishes.amazonOther],
              color: "var(--r-red)",
            },
            {
              label: "ailleurs",
              values: [
                affiliate.wishes.total - affiliate.wishes.amazonFr - affiliate.wishes.amazonOther,
              ],
              color: "var(--r-dim)",
            },
          ]}
        />
        <p className="mt-2" style={{ color: "var(--r-dim)" }}>
          Valeur cliquée amazon.fr / mois
        </p>
        <Columns
          labels={affiliate.months.map((month) => monthLabel(month.month))}
          height={80}
          format={(cents) => euros(cents)}
          series={[
            {
              label: "valeur",
              values: affiliate.months.map((month) => month.amazonFrValueCents),
              color: "var(--r-green)",
            },
          ]}
        />
      </Box>
      <Box
        title="amazon.fr par mois"
        className="md:col-span-2"
        right={affiliate.tagConfigured ? "tag actif" : "pas de tag : rien n'est gagné"}
      >
        <Table
          head={["mois", "clics", "amazon.fr", "autres amz", "produits", "panier moy.", "part"]}
          rows={affiliate.months.map((month) => [
            month.month,
            month.clicks,
            { value: n(month.amazonFr), tone: month.amazonFr > 0 ? "ok" : "dim" },
            month.amazonOther,
            month.amazonFrProducts,
            month.amazonFrPricedClicks > 0
              ? euros(month.amazonFrValueCents / month.amazonFrPricedClicks)
              : "-",
            percent(month.amazonFr, month.clicks),
          ])}
        />
      </Box>

      <Box title="estimation" right="3 derniers mois pleins">
        <label className="flex items-center gap-1">
          <span className="flex-1" style={{ color: "var(--r-dim)" }}>
            commission %
          </span>
          <input
            className="retro-input"
            type="number"
            min={0}
            max={20}
            step={0.5}
            value={commission}
            onChange={(event) => setCommission(Number(event.target.value))}
          />
        </label>
        <label className="mb-1 flex items-center gap-1">
          <span className="flex-1" style={{ color: "var(--r-dim)" }}>
            conversion clic→achat %
          </span>
          <input
            className="retro-input"
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={conversion}
            onChange={(event) => setConversion(Number(event.target.value))}
          />
        </label>
        <Row label="clics / mois">{estimate.clicksPerMonth.toFixed(1)}</Row>
        <Row label="ventes / mois">{estimate.salesPerMonth.toFixed(1)}</Row>
        <Row label="revenu / mois" tone="ok">
          {euros(estimate.revenueCentsPerMonth)}
        </Row>
        <Row label="revenu / an">{euros(estimate.revenueCentsPerMonth * 12)}</Row>
        <Row
          label={`ventes en ${QUALIFYING_DAYS} j (min ${QUALIFYING_SALES})`}
          tone={qualifies ? "ok" : "bad"}
        >
          {estimate.salesInWindow.toFixed(1)} {qualifies ? "✓" : "✗"}
        </Row>
        <p className="mt-1" style={{ color: "var(--r-dim)" }}>
          {qualifies
            ? "Le compte Partenaires tiendrait : ça vaut la demande."
            : "Pas assez de trafic pour garder un compte Partenaires ouvert, pour l'instant."}
        </p>
      </Box>

      <Box title="contexte">
        <Row label="clics amazon.fr 12 mois">{n(estimate.clicks12Months)}</Row>
        <Row label="tendance">
          <Spark values={affiliate.months.map((month) => month.amazonFr)} color="var(--r-amber)" />
        </Row>
        <Row label="envies sur amazon.fr">
          {n(affiliate.wishes.amazonFr)} (
          {percent(affiliate.wishes.amazonFr, affiliate.wishes.total)})
        </Row>
        <Row label="autres amazon / amzn.eu">{n(affiliate.wishes.amazonOther)}</Row>
        <Row label="panier moyen cliqué">
          {euros(estimate.basketCents)}
          {estimate.basketKnown ? "" : " (défaut)"}
        </Row>
        <p className="mt-1" style={{ color: "var(--r-dim)" }}>
          Le tag ne s'applique qu'à amazon.fr : amzn.eu et les autres pays n'en profitent pas.
        </p>
      </Box>

      <Box title="top amazon.fr" right="90 j" className="md:col-span-2 xl:col-span-3">
        <Table
          head={["produit", "prix", "clics"]}
          rows={affiliate.products.map((product) => [
            { value: product.title, num: false, title: product.title },
            product.priceCents === null ? "-" : euros(product.priceCents),
            product.clicks,
          ])}
        />
      </Box>
    </Grid>
  );
}
